import { MICROS_PER_UNIT, formatRupiah, paymentMethodLabel } from "./money";
import type { BillingIdentity, BillingIssuer, Receipt } from "./types";

/**
 * The date, fixed rather than localised.
 *
 * `absoluteTime` — what every screen in the dashboard uses — formats in the
 * *viewer's* locale and timezone, which is right for a screen and wrong for a
 * document. A file saved in Jakarta and opened in London would carry a different
 * date, and "08/16/2026" against "16/08/2026" is a real ambiguity between two
 * readers of the same invoice. A spelled month in UTC reads the same everywhere
 * and cannot be misread by either.
 *
 * No time of day. An invoice is dated, not timestamped, and a clock time invites
 * the question of whose clock.
 */
/**
 * The processor's name as it spells itself.
 *
 * Stored lowercase, and CSS `capitalize` — which the dashboard used to apply —
 * renders "Paypal", which is not the company's name. That is tolerable on a
 * screen and not on a document somebody files, so both use this instead.
 * Anything unrecognised keeps its stored spelling rather than being title-cased
 * into a different guess.
 */
export function processorName(provider: string): string {
  return (
    ({ paypal: "PayPal", polar: "Polar", stripe: "Stripe", borderpay: "BorderPay" } as Record<string, string>)[provider] ??
    provider
  );
}

export function invoiceDate(iso: string): string {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/**
 * The amount, with the currency named rather than symbolised.
 *
 * `formatMoney` renders `$50.00` in the viewer's locale. On a document that is
 * two problems: the platform bills one global currency and `$` belongs to at
 * least four of them, so a reader in Singapore cannot tell which dollar they
 * paid — and a reader in Germany would see `50,00 $`, a different string for the
 * same invoice. The ISO code is unambiguous to every accountant alive.
 */
export function invoiceAmount(micros: number, currency: string): string {
  const units = micros / MICROS_PER_UNIT;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(units);
  } catch {
    // An operator can configure any currency code, including one Intl rejects.
    return `${units.toFixed(2)} ${currency}`;
  }
}

/**
 * A receipt as a document somebody can file.
 *
 * Built as a standalone HTML string rather than rendered into the app, because
 * the two are read in different places: this one is saved, mailed to an
 * accountant, and opened months later in a browser that has never seen the
 * dashboard's stylesheet. Everything it needs is inline.
 *
 * **No tax line, and that is the platform's stated position rather than an
 * omission.** The Terms say the platform applies no tax handling and currency is
 * single and global, so the document says the same thing in a sentence a
 * bookkeeper can act on. Inventing a zero-rated tax line would imply a tax
 * treatment nobody has assessed; saying nothing at all would leave them
 * guessing. The customer's own registration number is printed when they have
 * recorded one, because that is what their filing needs.
 *
 * Issued against a *payment*, not a period. The platform runs on prepaid credit,
 * so what happened is "you added this much credit on this date" — describing it
 * as a month of service would be a different and untrue statement.
 */
export function invoiceHtml(args: {
  receipt: Receipt;
  issuer: BillingIssuer;
  customer: BillingIdentity;
  /** Falls back onto the document when the organization recorded no billing email. */
  fallbackEmail: string | null;
}): string {
  const { receipt, issuer, customer } = args;
  const amount = invoiceAmount(receipt.amountMicros, receipt.currency);
  const email = customer.email ?? args.fallbackEmail;

  // Escaped, because every one of these is user- or operator-supplied text and
  // this string is written straight into a document. A company name containing
  // an ampersand is ordinary; one containing a tag is not, and neither should
  // reach the page as markup.
  const escape = (value: string): string =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  // Newlines survive as line breaks: an address is written the way its country
  // writes it, which is the whole reason the field is free text.
  const lines = (value: string): string => escape(value).replace(/\n/g, "<br>");

  const processor = processorName(receipt.provider);
  // A payment charged in another currency says so on the document: the
  // customer's bank shows rupiah, the credit line shows dollars, and the
  // rate between them is the reconciliation.
  const charged = receipt.charged
    ? `${formatRupiah(receipt.charged.amount)}${paymentMethodLabel(receipt.charged.method) ? ` by ${paymentMethodLabel(receipt.charged.method)}` : ""}${receipt.charged.fxRate ? ` at ${formatRupiah(receipt.charged.fxRate)}/US$` : ""}`
    : null;

  const party = (
    heading: string,
    name: string,
    address: string,
    taxId: string | null,
    contact?: string | null,
  ): string => `
      <section class="party">
        <h2>${escape(heading)}</h2>
        <p class="name">${escape(name)}</p>
        <p>${lines(address)}</p>
        ${contact ? `<p>${escape(contact)}</p>` : ""}
        ${taxId ? `<p class="tax">Tax registration: ${escape(taxId)}</p>` : ""}
      </section>`;

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Invoice ${escape(receipt.number)}</title>
<style>
  :root { color-scheme: light; }
  body {
    margin: 0; padding: 48px;
    font: 14px/1.6 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
    color: #1a1a1a; background: #fff;
  }
  .sheet { max-width: 720px; margin: 0 auto; }
  header { display: flex; justify-content: space-between; align-items: flex-start;
           gap: 32px; border-bottom: 2px solid #1a1a1a; padding-bottom: 20px; }
  h1 { margin: 0; font-size: 26px; letter-spacing: -0.02em; }
  .meta { text-align: right; font-size: 13px; color: #555; }
  .meta p { margin: 0 0 2px; }
  /* The label carries the meaning, so it is quiet and always present: a bold
     number with nothing saying what it is makes a reader look for the caption. */
  .meta .key { color: #888; }
  .meta strong { color: #1a1a1a; }
  .parties { display: flex; gap: 48px; margin: 32px 0; }
  .party { flex: 1; }
  .party h2 { margin: 0 0 8px; font-size: 11px; letter-spacing: 0.08em;
              text-transform: uppercase; color: #777; font-weight: 600; }
  .party p { margin: 0 0 2px; }
  .party .name { font-weight: 600; }
  .party .tax { margin-top: 8px; font-size: 13px; color: #555; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 0; }
  th { text-align: left; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
       color: #777; border-bottom: 1px solid #ddd; padding: 0 0 8px; font-weight: 600; }
  th.right, td.right { text-align: right; }
  td { padding: 14px 0; border-bottom: 1px solid #eee; vertical-align: top; }
  .total { display: flex; justify-content: flex-end; gap: 32px;
           margin-top: 20px; font-size: 17px; font-weight: 600; }
  .note { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee;
          font-size: 12.5px; color: #555; }
  .note p { margin: 0 0 6px; }
  .paid { display: inline-block; margin-top: 4px; padding: 3px 10px; border-radius: 3px;
          background: #e7f5ec; color: #16653a; font-size: 12px; font-weight: 600;
          letter-spacing: 0.04em; text-transform: uppercase; }
  @media print { body { padding: 0; } .sheet { max-width: none; } }
</style>
</head>
<body>
  <div class="sheet">
    <header>
      <div>
        <h1>Invoice</h1>
        <span class="paid">Paid</span>
      </div>
      <div class="meta">
        <p><span class="key">Invoice no.</span> <strong>${escape(receipt.number)}</strong></p>
        <p><span class="key">Date issued</span> ${escape(invoiceDate(receipt.issuedAt))}</p>
      </div>
    </header>

    <div class="parties">
      ${party("From", issuer.name, issuer.address, issuer.taxId)}
      ${party("Billed to", customer.name ?? "", customer.address ?? "", customer.taxId, email)}
    </div>

    <table>
      <thead>
        <tr><th>Description</th><th class="right">Amount</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>
            Prepaid platform credit
            <br><span style="color:#777">Paid via ${escape(processor)}${charged ? ` · ${escape(charged)}` : ""} · reference ${escape(receipt.paymentId)}</span>
          </td>
          <td class="right">${escape(amount)}</td>
        </tr>
      </tbody>
    </table>

    <div class="total"><span>Total paid</span><span>${escape(amount)}</span></div>

    <div class="note">
      <p><strong>No tax has been applied to this amount.</strong> The platform applies no tax
         handling and currency is single and global; you are responsible for any taxes
         applicable to your own use of the services.</p>
      <p>This document records the purchase of prepaid credit, which is drawn down as
         applications, databases, scheduled jobs and inference are used.
         It does not cover a service period.</p>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Hands the document to the browser as a file.
 *
 * A client-side blob rather than a server round trip: everything on the page is
 * already in memory, so a request would only re-fetch what the caller is holding
 * — and there is no authenticated download path in this app any more.
 *
 * The object URL is revoked immediately; the download has been handed over by
 * then, and leaving it alive holds the file for the life of the tab.
 */
export function downloadInvoice(receipt: Receipt, html: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `invoice-${receipt.number}.html`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
